import { useParams } from "react-router-dom";

import { useDataset, useReads } from "../api/queries";
import { isStorageProblem, StorageProblem } from "../components/StorageProblem";
import { PageHeader } from "../components/layout/PageHeader";
import {
  EmptyBlock,
  ErrorBlock,
  LoadingBlock,
} from "../components/ui/StateBlock";
import { formatDateTime, pluralize } from "../lib/format";
import { PURPOSE_LABELS } from "../lib/metrics";

/**
 * Every read of this client's data: who, for what, when, and what happened.
 *
 * The refusals are the interesting half, so they are rows like any other with
 * the reason they carried. And the one thing the console cannot say, it says
 * out loud: it issues links and never sees the transfer.
 *
 * Returns:
 *     The *Who used this* screen.
 */
export function ReadsPage(): JSX.Element {
  const { id = "" } = useParams();
  const dataset = useDataset(id);
  const reads = useReads(id);

  const header = (
    <PageHeader
      eyebrow="Who used this"
      title={
        dataset.data
          ? `${dataset.data.account.name} · ${dataset.data.name}`
          : "Who used this"
      }
      back={{ to: `/datasets/${id}`, label: "Back to the dataset" }}
      subtitle="The console records that a link was issued, and to whom. It does not see the transfer."
    />
  );

  if (reads.isPending) {
    return (
      <>
        {header}
        <LoadingBlock label="Reading the ledger…" />
      </>
    );
  }

  if (isStorageProblem(reads.error)) {
    return (
      <>
        {header}
        <StorageProblem error={reads.error} onRetry={() => void reads.refetch()} />
      </>
    );
  }

  if (reads.isError) {
    return (
      <>
        {header}
        <ErrorBlock error={reads.error} onRetry={() => void reads.refetch()} />
      </>
    );
  }

  return (
    <>
      {header}

      <section className="section">
        <div className="section__head">
          <h2 className="section__title">
            {pluralize(reads.data.total, "request")}, granted and refused
          </h2>
        </div>

        {reads.data.items.length === 0 ? (
          <EmptyBlock label="Nobody has asked for this client's file yet." />
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th scope="col">Who</th>
                <th scope="col">For what</th>
                <th scope="col">Version</th>
                <th scope="col">What happened</th>
                <th scope="col">When</th>
              </tr>
            </thead>
            <tbody>
              {reads.data.items.map((read) => (
                <tr key={read.id}>
                  <td>
                    <span className="admin-table__name">{read.actor}</span>
                    <span className="admin-table__sub">
                      {read.via === "key"
                        ? `a key${read.key_prefix ? ` — dk_${read.key_prefix}…` : ""}`
                        : "from the console in a browser"}
                    </span>
                  </td>
                  <td>
                    {read.purpose ? PURPOSE_LABELS[read.purpose] : "no purpose named"}
                    {read.note ? (
                      <span className="admin-table__sub">{read.note}</span>
                    ) : null}
                  </td>
                  <td className="mono">{read.version_number}</td>
                  <td>
                    {read.granted ? (
                      <>
                        <span className="badge" data-tone="ok">
                          a link was issued
                        </span>
                        {read.url_expires_at ? (
                          <span className="admin-table__sub">
                            it lapsed {formatDateTime(read.url_expires_at)}
                          </span>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <span className="badge" data-tone="danger">
                          refused
                        </span>
                        <span className="admin-table__sub mono">
                          {read.refusal_code}
                        </span>
                      </>
                    )}
                  </td>
                  <td className="admin-table__muted">
                    {formatDateTime(read.at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <p className="form__note">
          There is no column here for whether anybody downloaded anything. The
          console hands out a link and the bytes travel from storage to the
          reader without passing through it, so it does not know — and a column
          that was always empty would look like a promise to fill it in.
        </p>
      </section>
    </>
  );
}
